// Count-mode cell modal — FE-SCRM-008.
//
// Opens when a producer clicks a count tile in the grid. Shows the
// full unbounded opportunity list for that (month, stage) cell with
// secondary filters and sort options. Closing the modal returns to
// the board with full state preserved (filters / geometry / scroll).

import {
  CADENCE_BG_DOT, CADENCE_LABEL,
} from "./cadenceColors";
import type { RenewalBoardCellResponse } from "./types";


type SortKey =
  | "cadence_health_severity"
  | "days_until_renewal"
  | "value_estimate"
  | "prospect_name";


interface Props {
  target: { month: string; stage: string };
  data: RenewalBoardCellResponse | undefined;
  is_loading: boolean;
  on_close: () => void;
  on_opportunity_click: (id: string) => void;
  sort: SortKey;
  on_sort_change: (next: SortKey) => void;
  min_lives: number | null;
  on_min_lives_change: (next: number | null) => void;
}


const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];


function formatMonth(key: string): string {
  const [y, m] = key.split("-");
  const idx = Number(m) - 1;
  return `${MONTH_NAMES[idx] ?? m} ${y}`;
}


function formatStage(stage: string): string {
  return stage
    .split("_").map((s) => s[0]?.toUpperCase() + s.slice(1))
    .join(" ");
}


export function CellModal({
  target, data, is_loading, on_close, on_opportunity_click,
  sort, on_sort_change, min_lives, on_min_lives_change,
}: Props) {
  return (
    <div
      data-testid="cell-modal"
      role="dialog"
      aria-label={`Cell detail for ${formatStage(target.stage)} in ${formatMonth(target.month)}`}
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"
      onClick={on_close}
    >
      <div
        className="bg-raised rounded-lg shadow-xl w-[640px] max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-border-subtle p-4">
          <div>
            <h2 className="text-base font-semibold text-text-primary">
              {formatStage(target.stage)} renewing in {formatMonth(target.month)}
            </h2>
            {data && (
              <div className="text-xs text-text-tertiary">
                {data.total} opportunit{data.total === 1 ? "y" : "ies"}
              </div>
            )}
          </div>
          <button
            type="button"
            data-testid="cell-modal-close"
            onClick={on_close}
            className="text-text-tertiary hover:text-text-primary text-xl px-2"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="flex items-center gap-3 px-4 py-2 border-b border-border-subtle text-xs">
          <label className="flex items-center gap-1">
            <span className="text-text-tertiary">Sort by</span>
            <select
              data-testid="cell-modal-sort"
              value={sort}
              onChange={(e) => on_sort_change(e.target.value as SortKey)}
              className="bg-canvas border border-border-subtle rounded px-1 py-0.5"
            >
              <option value="cadence_health_severity">
                Cadence severity
              </option>
              <option value="days_until_renewal">Days to renewal</option>
              <option value="value_estimate">Value</option>
              <option value="prospect_name">Prospect name</option>
            </select>
          </label>
          <label className="flex items-center gap-1 ml-2">
            <span className="text-text-tertiary">Min lives</span>
            <input
              type="number"
              data-testid="cell-modal-min-lives"
              value={min_lives ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                on_min_lives_change(v === "" ? null : Number(v));
              }}
              min={0}
              className="w-16 bg-canvas border border-border-subtle rounded px-1 py-0.5 tabular-nums"
            />
          </label>
        </div>

        <div className="overflow-y-auto p-3 flex-1">
          {is_loading ? (
            <div className="text-xs text-text-tertiary italic text-center py-6">
              Loading…
            </div>
          ) : !data || data.rows.length === 0 ? (
            <div className="text-xs text-text-tertiary italic text-center py-6">
              No opportunities match the secondary filters.
            </div>
          ) : (
            <ul className="flex flex-col gap-1">
              {data.rows.map((row) => (
                <li key={row.opportunity_id}>
                  <button
                    type="button"
                    data-testid={`cell-modal-row-${row.opportunity_id}`}
                    onClick={() => on_opportunity_click(row.opportunity_id)}
                    className="w-full text-left flex items-center gap-3 px-2 py-2 rounded hover:bg-recessed"
                  >
                    <span
                      className={[
                        "inline-block w-2 h-2 rounded-full shrink-0",
                        CADENCE_BG_DOT[row.cadence_health],
                      ].join(" ")}
                      title={CADENCE_LABEL[row.cadence_health]}
                    />
                    <span className="text-sm text-text-primary flex-1 truncate">
                      {row.prospect_name}
                    </span>
                    <span className="text-xs text-text-tertiary">
                      {row.opportunity_type.replace("_", " ")}
                    </span>
                    <span className="text-xs text-text-tertiary tabular-nums">
                      {row.days_until_renewal}d
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
